import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { bookingService } from '@/services/bookingService';
import SaudiRiyal from '@/components/shared/saudi-riyal';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import {
  User,
  MapPin,
  Calendar,
  CreditCard,
  Users,
  FileText,
  Phone,
  Mail,
  Clock,
  DollarSign,
  CheckCircle,
  XCircle,
  AlertCircle,
} from 'lucide-react';

interface BookingDetailsDialogProps {
  bookingId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

interface BookingDetails {
  id: string;
  userId: string;
  startDate: string;
  endDate: string;
  status: string;
  payment_status: string;
  couponCode: string | null;
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
  property_id: number;
  total_price: string;
  deposit_amount: string;
  deposit_paid: string;
  remaining_amount: string;
  payment_method: string;
  payment_reference: string | null;
  check_in: string;
  check_out: string;
  guest_count: number;
  special_requests: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  couponId: string | null;
  User: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
  Property: {
    id: number;
    name: string;
  };
}

export function BookingDetailsDialog({ bookingId, isOpen, onClose }: BookingDetailsDialogProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [loading, setLoading] = useState(false);

  const isArabic = i18n.language === 'ar';

  useEffect(() => {
    if (bookingId && isOpen) {
      fetchBookingDetails();
    }
  }, [bookingId, isOpen]);

  const fetchBookingDetails = async () => {
    if (!bookingId) return;

    try {
      setLoading(true);
      const response = await bookingService.getBookingDetails(bookingId);
      
      if (response.success && response.data) {
        setBooking(response.data);
      } else {
        toast({
          title: t('error') || 'خطأ',
          description: t('failedToLoadBookingDetails') || 'فشل في تحميل تفاصيل الحجز',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error fetching booking details:', error);
      toast({
        title: t('error') || 'خطأ',
        description: t('failedToLoadBookingDetails') || 'فشل في تحميل تفاصيل الحجز',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { 
        variant: 'secondary' as const, 
        icon: AlertCircle, 
        text: t('pending') || 'قيد الانتظار',
        color: 'text-yellow-600'
      },
      confirmed: { 
        variant: 'default' as const, 
        icon: CheckCircle, 
        text: t('confirmed') || 'مؤكد',
        color: 'text-blue-600'
      },
      completed: { 
        variant: 'default' as const, 
        icon: CheckCircle, 
        text: t('completed') || 'مكتمل',
        color: 'text-green-600'
      },
      cancelled: { 
        variant: 'destructive' as const, 
        icon: XCircle, 
        text: t('cancelled') || 'ملغي',
        color: 'text-red-600'
      },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.text}
      </Badge>
    );
  };

  const getPaymentStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { 
        variant: 'secondary' as const, 
        text: t('paymentPending') || 'دفع معلق',
        color: 'text-yellow-600'
      },
      paid: { 
        variant: 'default' as const, 
        text: t('paid') || 'مدفوع',
        color: 'text-green-600'
      },
      refunded: { 
        variant: 'destructive' as const, 
        text: t('refunded') || 'مسترد',
        color: 'text-red-600'
      },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

    return (
      <Badge variant={config.variant}>
        {config.text}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'PPP', { locale: isArabic ? ar : undefined });
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'PPP p', { locale: isArabic ? ar : undefined });
    } catch {
      return dateString;
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t('bookingDetails') || 'تفاصيل الحجز'}
            {booking && (
              <span className="text-muted-foreground">#{booking.id.slice(-8)}</span>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">{t('loading') || 'جاري التحميل'}...</div>
          </div>
        ) : booking ? (
          <div className="space-y-6">
            {/* Status and Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    {t('bookingStatus') || 'حالة الحجز'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('status') || 'الحالة'}:</span>
                    {getStatusBadge(booking.status)}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('paymentStatus') || 'حالة الدفع'}:</span>
                    {getPaymentStatusBadge(booking.payment_status)}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('paymentMethod') || 'طريقة الدفع'}:</span>
                    <span className="font-medium">{booking.payment_method}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    {t('bookingDates') || 'تواريخ الحجز'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('checkIn') || 'تاريخ الوصول'}:</span>
                    <span className="font-medium">{formatDateTime(booking.check_in)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('checkOut') || 'تاريخ المغادرة'}:</span>
                    <span className="font-medium">{formatDateTime(booking.check_out)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('guests') || 'عدد الضيوف'}:</span>
                    <span className="font-medium flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {booking.guest_count}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Customer Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-5 w-5" />
                  {t('customerInformation') || 'معلومات العميل'}
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{booking.User.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{booking.User.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{booking.User.phone}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground">{t('userId') || 'معرف المستخدم'}:</div>
                  <div className="text-xs font-mono bg-muted p-2 rounded">{booking.User.id}</div>
                </div>
              </CardContent>
            </Card>

            {/* Property Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  {t('propertyInformation') || 'معلومات العقار'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{booking.Property.name}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {t('propertyId') || 'معرف العقار'}: {booking.Property.id}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Financial Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  {t('financialDetails') || 'التفاصيل المالية'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('originalAmount') || 'المبلغ الأصلي'}:</span>
                      <SaudiRiyal amount={booking.originalAmount} variant="new" />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('discountAmount') || 'مبلغ الخصم'}:</span>
                      <SaudiRiyal amount={booking.discountAmount} variant="new" />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between font-medium">
                      <span>{t('finalAmount') || 'المبلغ النهائي'}:</span>
                      <SaudiRiyal amount={booking.finalAmount} variant="new" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('depositAmount') || 'مبلغ العربون'}:</span>
                      <SaudiRiyal amount={parseFloat(booking.deposit_amount)} variant="new" />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('depositPaid') || 'العربون المدفوع'}:</span>
                      <SaudiRiyal amount={parseFloat(booking.deposit_paid)} variant="new" />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('remainingAmount') || 'المبلغ المتبقي'}:</span>
                      <SaudiRiyal amount={parseFloat(booking.remaining_amount)} variant="new" />
                    </div>
                  </div>
                </div>
                
                {booking.couponCode && (
                  <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-green-700 dark:text-green-300">
                        {t('couponApplied') || 'تم تطبيق كوبون'}: {booking.couponCode}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Special Requests and Notes */}
            {(booking.special_requests || booking.notes) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    {t('additionalInformation') || 'معلومات إضافية'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {booking.special_requests && (
                    <div>
                      <div className="text-sm font-medium text-muted-foreground mb-2">
                        {t('specialRequests') || 'طلبات خاصة'}:
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        {booking.special_requests}
                      </div>
                    </div>
                  )}
                  {booking.notes && (
                    <div>
                      <div className="text-sm font-medium text-muted-foreground mb-2">
                        {t('notes') || 'ملاحظات'}:
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        {booking.notes}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Booking Metadata */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  {t('bookingMetadata') || 'بيانات الحجز'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('bookingId') || 'رقم الحجز'}:</span>
                  <span className="text-xs font-mono bg-muted p-1 rounded">{booking.id}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('createdAt') || 'تاريخ الإنشاء'}:</span>
                  <span className="text-sm">{formatDateTime(booking.createdAt)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('lastUpdated') || 'آخر تحديث'}:</span>
                  <span className="text-sm">{formatDateTime(booking.updatedAt)}</span>
                </div>
                {booking.payment_reference && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('paymentReference') || 'مرجع الدفع'}:</span>
                    <span className="text-xs font-mono bg-muted p-1 rounded">{booking.payment_reference}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">{t('bookingNotFound') || 'لم يتم العثور على الحجز'}</div>
          </div>
        )}

        <div className="flex justify-end pt-4">
          <Button onClick={onClose} variant="outline">
            {t('close') || 'إغلاق'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
